/**
 * @file path-node-editor.ts
 * @description State machine for interactive SVG path node editing.
 * No DOM access — pure logic that emits EventBus events.
 *
 * Consumers must call:
 *   editor.activate(shapeId, svgPathString)  — to start editing
 *   editor.onMouseDown / onMouseMove / onMouseUp — forwarded from event-dispatcher
 *   editor.commit() / editor.deactivate()    — to save or discard
 */

import { EventBus } from '@hcie/shared';
import type { BezierNode, PathData, PathHitResult, Point2D } from '@hcie/shared';
import { parseSvgPath, serializePath, findClosestT, cubicBezierPoint } from '@hcie/shared';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Hit-test radii in screen-space pixels (divided by zoom before use). */
const ANCHOR_HIT_RADIUS = 8;
const HANDLE_HIT_RADIUS = 6;
const SEGMENT_HIT_RADIUS = 5;

// ─── Internal helpers ─────────────────────────────────────────────────────────

function dist2(ax: number, ay: number, bx: number, by: number): number {
    const dx = ax - bx, dy = ay - by;
    return dx * dx + dy * dy;
}

// ─── PathNodeEditor ───────────────────────────────────────────────────────────

export class PathNodeEditor {
    private shapeId: string | null = null;
    private pathData: PathData | null = null;
    private selectedNodeIndex = -1;
    private selectedHandle: 'anchor' | 'cp1' | 'cp2' | null = null;
    private dragStartX = 0;
    private dragStartY = 0;
    private zoom = 1;
    private _isDragging = false;

    // ── Public lifecycle ──────────────────────────────────────────────────────

    /** Activates editing for a specific shape. Emits PATH_EDIT_ACTIVATED. */
    public activate(shapeId: string, svgPathString: string): void {
        this.shapeId = shapeId;
        this.pathData = parseSvgPath(svgPathString);
        this.selectedNodeIndex = -1;
        this.selectedHandle = null;
        this._isDragging = false;

        EventBus.emit('PATH_EDIT_ACTIVATED', {
            shapeId,
            nodes: this.pathData.nodes,
            isClosed: this.pathData.isClosed,
        });
    }

    /** Discards unsaved changes and emits PATH_EDIT_DEACTIVATED. */
    public deactivate(): void {
        this.shapeId = null;
        this.pathData = null;
        this.selectedNodeIndex = -1;
        this.selectedHandle = null;
        this._isDragging = false;
        EventBus.emit('PATH_EDIT_DEACTIVATED', {});
    }

    /** Saves the current state and emits PATH_NODES_CHANGED. */
    public commit(): void {
        if (!this.pathData || !this.shapeId) return;
        EventBus.emit('PATH_NODES_CHANGED', {
            shapeId: this.shapeId,
            pathData: serializePath(this.pathData),
        });
    }

    /** Updates the zoom level so hit-test radii scale correctly. */
    public setZoom(zoom: number): void {
        this.zoom = Math.max(0.01, zoom);
    }

    public get isActive(): boolean {
        return this.shapeId !== null;
    }

    // ── Input handling ────────────────────────────────────────────────────────

    /**
     * Handles canvas mousedown.
     * @returns true if a node or handle was hit (caller should stop propagation).
     */
    public onMouseDown(x: number, y: number): boolean {
        if (!this.pathData) return false;

        const hit = this._hitTest(x, y);
        if (hit.type === 'none') {
            this.selectedNodeIndex = -1;
            this.selectedHandle = null;
            this._emitOverlayUpdate();
            return false;
        }

        this.selectedNodeIndex = hit.nodeIndex;
        this.selectedHandle = hit.type === 'anchor' ? 'anchor'
            : hit.type === 'cp1' ? 'cp1' : 'cp2';
        this.dragStartX = x;
        this.dragStartY = y;
        this._isDragging = true;
        this._emitOverlayUpdate();
        return true;
    }

    /**
     * Handles canvas mousemove during drag.
     * dx/dy are the total displacement from the drag start point.
     */
    public onMouseMove(x: number, y: number): void {
        if (!this._isDragging || !this.pathData) return;
        const dx = x - this.dragStartX;
        const dy = y - this.dragStartY;
        this.dragStartX = x;
        this.dragStartY = y;
        this._moveSelected(dx, dy);
        this._emitOverlayUpdate();
    }

    /** Handles mouseup — commits the incremental change. */
    public onMouseUp(): void {
        if (!this._isDragging) return;
        this._isDragging = false;
        this.commit();
    }

    // ── Node operations ───────────────────────────────────────────────────────

    /** Deletes the selected node (min 2 nodes required). */
    public deleteSelectedNode(): void {
        if (!this.pathData || this.selectedNodeIndex < 0) return;
        if (this.pathData.nodes.length <= 2) return;
        this.pathData.nodes.splice(this.selectedNodeIndex, 1);
        this.selectedNodeIndex = Math.min(this.selectedNodeIndex, this.pathData.nodes.length - 1);
        this.commit();
        this._emitOverlayUpdate();
    }

    /**
     * Inserts a new node on the segment closest to (x, y).
     * Uses findClosestT to locate the best insertion point.
     */
    public addNodeAt(x: number, y: number): void {
        if (!this.pathData) return;
        const nodes = this.pathData.nodes;
        const len = nodes.length;
        if (len < 2) return;

        let bestSegment = 0;
        let bestT = 0;
        let bestDist = Infinity;

        const end = this.pathData.isClosed ? len : len - 1;
        for (let i = 0; i < end; i++) {
            const a = nodes[i];
            const b = nodes[(i + 1) % len];
            const cp1 = a.cp2 ?? a;
            const cp2 = b.cp1 ?? b;
            const { t, distance } = findClosestT(a, cp1, cp2, b, { x, y });
            if (distance < bestDist) {
                bestDist = distance;
                bestSegment = i;
                bestT = t;
            }
        }

        const a = nodes[bestSegment];
        const b = nodes[(bestSegment + 1) % len];
        const cp1 = a.cp2 ?? a;
        const cp2 = b.cp1 ?? b;
        const pt = cubicBezierPoint(a, cp1, cp2, b, bestT);
        const newNode: BezierNode = {
            id: Math.random().toString(36).slice(2, 9),
            x: pt.x, y: pt.y,
            nodeType: 'smooth',
        };
        nodes.splice(bestSegment + 1, 0, newNode);
        this.selectedNodeIndex = bestSegment + 1;
        this.commit();
        this._emitOverlayUpdate();
    }

    /** Cycles the selected node's type: corner → smooth → symmetric → corner. */
    public cycleSelectedNodeType(): void {
        if (!this.pathData || this.selectedNodeIndex < 0) return;
        const node = this.pathData.nodes[this.selectedNodeIndex];
        node.nodeType = node.nodeType === 'corner' ? 'smooth'
            : node.nodeType === 'smooth' ? 'symmetric' : 'corner';
        this.commit();
        this._emitOverlayUpdate();
    }

    /** Returns a copy of the current nodes for overlay rendering. */
    public getNodes(): BezierNode[] {
        return this.pathData?.nodes ?? [];
    }

    public isClosed(): boolean {
        return this.pathData?.isClosed ?? false;
    }

    public getSelectedIndex(): number {
        return this.selectedNodeIndex;
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    private _hitTest(x: number, y: number): PathHitResult {
        if (!this.pathData) return { type: 'none', nodeIndex: -1 };
        const nodes = this.pathData.nodes;
        const anchorR2 = (ANCHOR_HIT_RADIUS / this.zoom) ** 2;
        const handleR2 = (HANDLE_HIT_RADIUS / this.zoom) ** 2;
        const segR    = SEGMENT_HIT_RADIUS / this.zoom;

        // Check anchors first (higher priority)
        for (let i = 0; i < nodes.length; i++) {
            if (dist2(x, y, nodes[i].x, nodes[i].y) <= anchorR2) {
                return { type: 'anchor', nodeIndex: i };
            }
        }

        // Check control handles
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].cp1 && dist2(x, y, nodes[i].cp1!.x, nodes[i].cp1!.y) <= handleR2) {
                return { type: 'cp1', nodeIndex: i };
            }
            if (nodes[i].cp2 && dist2(x, y, nodes[i].cp2!.x, nodes[i].cp2!.y) <= handleR2) {
                return { type: 'cp2', nodeIndex: i };
            }
        }

        // Check segments
        const end = this.pathData.isClosed ? nodes.length : nodes.length - 1;
        for (let i = 0; i < end; i++) {
            const a = nodes[i];
            const b = nodes[(i + 1) % nodes.length];
            const cp1 = a.cp2 ?? a;
            const cp2 = b.cp1 ?? b;
            const { t, distance } = findClosestT(a, cp1, cp2, b, { x, y });
            if (distance <= segR) {
                return { type: 'segment', nodeIndex: i, segmentT: t };
            }
        }

        return { type: 'none', nodeIndex: -1 };
    }

    private _moveSelected(dx: number, dy: number): void {
        if (!this.pathData || this.selectedNodeIndex < 0) return;
        const node = this.pathData.nodes[this.selectedNodeIndex];

        if (this.selectedHandle === 'anchor') {
            // Move anchor + all its handles together
            node.x += dx;
            node.y += dy;
            if (node.cp1) { node.cp1.x += dx; node.cp1.y += dy; }
            if (node.cp2) { node.cp2.x += dx; node.cp2.y += dy; }
        } else if (this.selectedHandle === 'cp1') {
            if (!node.cp1) return;
            node.cp1.x += dx;
            node.cp1.y += dy;
            this._mirrorHandle(node, 'cp1');
        } else if (this.selectedHandle === 'cp2') {
            if (!node.cp2) return;
            node.cp2.x += dx;
            node.cp2.y += dy;
            this._mirrorHandle(node, 'cp2');
        }
    }

    /**
     * Applies handle symmetry rules after moving a control handle.
     * - symmetric: mirror both length and direction
     * - smooth:    mirror direction only (preserves other handle length)
     * - corner:    no mirroring
     */
    private _mirrorHandle(node: BezierNode, moved: 'cp1' | 'cp2'): void {
        if (node.nodeType === 'corner') return;

        const movedHandle = moved === 'cp1' ? node.cp1 : node.cp2;
        const otherKey    = moved === 'cp1' ? 'cp2' : 'cp1';
        const other       = node[otherKey];
        if (!movedHandle || !other) return;

        const mvdDx = movedHandle.x - node.x;
        const mvdDy = movedHandle.y - node.y;
        const mvdLen = Math.sqrt(mvdDx * mvdDx + mvdDy * mvdDy) || 1;

        if (node.nodeType === 'symmetric') {
            other.x = node.x - mvdDx;
            other.y = node.y - mvdDy;
        } else {
            // smooth: keep other handle's length, mirror direction
            const otherLen = Math.sqrt(
                (other.x - node.x) ** 2 + (other.y - node.y) ** 2
            ) || mvdLen;
            other.x = node.x - (mvdDx / mvdLen) * otherLen;
            other.y = node.y - (mvdDy / mvdLen) * otherLen;
        }
    }

    private _emitOverlayUpdate(): void {
        if (!this.pathData) return;
        EventBus.emit('PATH_OVERLAY_UPDATE', {
            nodes: this.pathData.nodes,
            isClosed: this.pathData.isClosed,
            selectedIndex: this.selectedNodeIndex,
        });
    }
}
